import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Clock, AlertTriangle, Check, Sparkles, Zap, Cpu, FileText, GitBranch, Building2, Plus } from 'lucide-react';
import { RoiCalculator } from '../LandingExtras';
import EngineShowcase from '../EngineShowcase';
import axios from 'axios';
import API from '../apiConfig';

function usePricing() {
  const [pricing, setPricing] = useState({ owner_price: 269.99, member_price: 149.99, free_analyses: 1 });
  useEffect(() => {
    axios.get(`${API}/api/pricing`).then(r => setPricing(r.data)).catch(() => {});
  }, []);
  return pricing;
}

/* Parallax-Section: Elemente gleiten sanft mit 3D-Tiefe ins Bild */
function ScrollReveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0) translateZ(0)';
          io.unobserve(el);
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: 0,
        transform: 'translateY(48px) translateZ(-30px)',
        transition: `opacity 0.8s cubic-bezier(.22,1,.36,1) ${delay}s, transform 0.8s cubic-bezier(.22,1,.36,1) ${delay}s`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </div>
  );
}

const PROBLEMS = [
  { icon: Clock, title: 'Stunden bis Tage manueller Aufwand', desc: 'Jede Position eines klassischen Leistungsverzeichnisses wird per Hand aus dem Plan abgegriffen — Wandlängen messen, Mengen rechnen, Texte tippen.' },
  { icon: AlertTriangle, title: 'Fehleranfällige Massenermittlung', desc: 'Vergessene Wände, falsche Wandstärken, doppelt gezählte Räume. Ein Fehler im LV kostet im Bauablauf das Vielfache.' },
  { icon: GitBranch, title: 'Keine Skalierung über Projekte hinweg', desc: 'Standardpositionen werden in jedem Büro neu erfunden, Vorlagen veralten, der nächste Mitarbeiter beginnt von vorn.' },
];

const SOLUTION_STEPS = [
  { num: '01', title: 'PDF-Grundriss hochladen', desc: 'Wir lesen Vektoren, Maßketten und Geometrien direkt aus Ihrer Plan-Datei aus — ohne dass Sie eine einzige Linie nachzeichnen müssen.' },
  { num: '02', title: 'Engine analysiert in Sekunden', desc: 'Räume, Außen- und Innenwände, Türen, Fenster und Bemaßungen werden automatisch erkannt, klassifiziert und DIN-konform kalibriert.' },
  { num: '03', title: 'Im Editor prüfen & anpassen', desc: 'Im interaktiven Bearbeitungstool können Sie alles nach Belieben anpassen — Wände manuell einzeichnen, Polygone verschieben, Räume umbenennen, Sonderpositionen hinzufügen. Kein Ergebnis wird Ihnen aufgezwungen.' },
  { num: '04', title: 'Leistungsverzeichnis exportieren', desc: 'Wandflächen, Volumen und Positionen werden gruppiert und als VOB-konformes Langtext-LV exportiert. Mengen-genau, vergabefertig.' },
];

const FEATURES = [
  { icon: Cpu, title: 'Automatische Grundrissanalyse', desc: 'PDF rein, strukturierte Daten raus. Räume, Wände, Öffnungen — pixelgenau, klassifiziert und auf DIN-Standards normiert.' },
  { icon: FileText, title: 'Strukturierte LV-Erstellung', desc: 'Gruppierte Mauerwerks-Positionen mit m² + m³, manuelle Sonderpositionen mit L/B/H-Rechner, alles im VOB/C-Format.' },
  { icon: Zap, title: 'Hohe Geschwindigkeit', desc: 'Was im Büro Tage dauert, läuft bei uns in unter 60 Sekunden. Die Engine arbeitet, während Sie den Kaffee holen.' },
  { icon: Sparkles, title: 'Kontinuierliche Weiterentwicklung', desc: 'Jede Anregung aus der Praxis fließt in das nächste Release. Sie arbeiten nie mit veralteter Software.' },
];

const TRUST = [
  { num: '< 60s', label: 'Analyse-Dauer pro Geschoss' },
  { num: '< 1 %', label: 'Mengen-Abweichung zur ING-Wandliste' },
  { num: 'VOB/C', label: 'DIN 18330 konform' },
  { num: 'EU', label: 'Hosting & DSGVO' },
];

export default function HomePage({ onLogin, onRegister, navigate }) {
  const pricing = usePricing();
  const fmt = (n) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const teamExample = fmt(pricing.owner_price + 4 * pricing.member_price);

  return (
    <div style={{ perspective: '1200px', perspectiveOrigin: '50% 30%' }}>
      {/* HERO */}
      <section className="pt-32 pb-20 px-6">
        <ScrollReveal>
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-xs font-medium mb-8">
            <Sparkles className="w-3.5 h-3.5" /> Software-Engine für die Bauplanung
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Leistungsverzeichnisse <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">automatisch aus Grundrissen</span> erstellen
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Schluss mit stundenlanger Mengenermittlung. LAIzyBlueprint liest Ihre Pläne in Sekunden ein und erstellt
            ein vollständiges, VOB-konformes Leistungsverzeichnis — präzise, konsistent und vergabefertig.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-3">
            <button
              onClick={onRegister}
              data-testid="hero-register-btn"
              className="px-8 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-all hover:shadow-lg hover:shadow-blue-500/20 flex items-center gap-2 text-sm"
            >
              Jetzt testen <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => document.getElementById('roi')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-roi-btn"
              className="px-8 py-3.5 border border-zinc-700 hover:border-zinc-500 text-zinc-200 font-medium rounded-lg transition-colors text-sm"
            >
              ROI berechnen
            </button>
          </div>
          <span className="text-zinc-600 text-xs">Erste Analyse kostenlos · keine Kreditkarte nötig</span>
        </div>
        </ScrollReveal>
      </section>

      {/* TRUST BAR */}
      <ScrollReveal>
      <section className="py-8 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {TRUST.map((t, i) => (
            <div key={i}>
              <div className="text-2xl sm:text-3xl font-bold text-blue-400" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{t.num}</div>
              <div className="text-xs text-zinc-500 mt-1">{t.label}</div>
            </div>
          ))}
        </div>
      </section>
      </ScrollReveal>

      {/* PROBLEM */}
      <ScrollReveal>
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-3">Das Problem</div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Klassische LV-Erstellung kostet, was sie nicht kosten dürfte.
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Bauunternehmen, Architekten und Planungsbüros verlieren Wochen pro Projekt allein durch manuelles
              Massenrechnen aus 2D-Plänen. Das ist 2026 nicht mehr nötig.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PROBLEMS.map((p, i) => (
              <div key={i} className="p-6 bg-zinc-900/50 border border-zinc-800/60 rounded-xl">
                <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center mb-4">
                  <p.icon className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="font-semibold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{p.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* SOLUTION */}
      <ScrollReveal>
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-3">Die Lösung</div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Eine Engine, die liest, versteht und ausgibt.
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Skalierbar, präzise und vom ersten Tag an einsatzbereit — ohne Setup-Wochen und ohne Schulungen.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SOLUTION_STEPS.map((s, i) => (
              <div key={i} className="relative p-7 bg-zinc-900 border border-zinc-800 rounded-xl">
                <div className="absolute -top-4 left-6 px-2.5 py-0.5 bg-blue-500 text-white font-mono text-xs rounded">
                  {s.num}
                </div>
                <h3 className="font-semibold text-white mb-3 mt-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {s.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* ENGINE SHOWCASE — Plato-Style scrollytelling with real screenshots */}
      <ScrollReveal>
      <EngineShowcase />
      </ScrollReveal>

      {/* EDITOR — Zwischenposition zwischen Erkennung und LV */}
      <ScrollReveal>
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-3">Sie behalten die Kontrolle</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Zwischen Erkennung und LV: <br />Ihr interaktiver Editor.
              </h2>
              <p className="text-zinc-300 leading-relaxed mb-5">
                Nach der automatischen Analyse landen Sie nicht direkt im fertigen LV — Sie kommen in unser
                <strong className="text-white"> Bearbeitungstool</strong>. Hier können Sie nach Belieben anpassen,
                korrigieren und ergänzen, bevor das Leistungsverzeichnis exportiert wird.
              </p>
              <ul className="space-y-3 mb-7">
                {[
                  'Wände, Räume, Türen und Fenster manuell einzeichnen',
                  'Polygone und Vertices per Drag & Drop verschieben',
                  'Wandstärken und Bauteile umklassifizieren',
                  'Sonderpositionen wie Streifenfundament oder Attika manuell hinzufügen',
                  'Schnellrechner für L × B × H, Fläche × Höhe, Volumen',
                  'Multi-Selektion mit Ctrl+Klick für Massen-Edits',
                  'Erst wenn Sie zufrieden sind, exportieren Sie das LV',
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-zinc-200">
                    <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className="text-zinc-500 text-sm leading-relaxed italic">
                Kein Ergebnis wird Ihnen aufgezwungen. Sie sehen jeden erkannten Wert, können jeden Wert
                korrigieren und behalten die volle fachliche Kontrolle — die Engine schlägt vor, Sie
                entscheiden.
              </p>
            </div>
            <div className="relative rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 shadow-2xl shadow-blue-500/5">
              <div className="absolute top-2.5 left-2.5 z-10 flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-zinc-700" />
                <span className="w-2 h-2 rounded-full bg-zinc-700" />
                <span className="w-2 h-2 rounded-full bg-zinc-700" />
              </div>
              <div className="absolute top-2.5 right-2.5 z-10 px-2 py-0.5 bg-zinc-950/70 backdrop-blur border border-zinc-700/50 rounded font-mono text-[9px] text-zinc-300">
                Editor-Modus
              </div>
              <img
                src="https://customer-assets.emergentagent.com/job_827345f2-6547-45f4-8537-fd6820f9b630/artifacts/io344u6e_GUTTTTT.png"
                alt="LAIzyBlueprint Editor mit interaktiver Bearbeitung"
                loading="lazy"
                className="w-full h-auto block bg-zinc-950"
              />
            </div>
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* FEATURES */}
      <ScrollReveal>
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-3">Features</div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Vom ersten PDF bis zum letzten Komma.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="p-7 bg-zinc-900/50 border border-zinc-800/60 rounded-xl hover:border-zinc-700/60 transition-all">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="font-semibold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{f.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* STORY */}
      <ScrollReveal>
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-3">Unsere Geschichte</div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Aus echtem Bedarf entstanden.
          </h2>
          <div className="space-y-5 text-zinc-300 leading-relaxed">
            <p>
              LAIzyBlueprint entstand nicht am Reißbrett, sondern in der Werkstatt eines bestehenden Kunden — einem
              Bauunternehmen, das schlicht keine Lust mehr hatte, jede Wandfläche von Hand zu zählen.
            </p>
            <p>
              In enger Zusammenarbeit mit Bauunternehmen, Baustoffhändlern und Handwerkern haben wir die Engine über
              hunderte echte Pläne hinweg geschärft. Was nicht in der Praxis besteht, kommt nicht ins Produkt.
            </p>
            <p>
              Heute setzen wir die gewonnenen Erkenntnisse Tag für Tag um — jede neue Funktion ist eine direkte
              Antwort auf eine reale Frage von der Baustelle, vom Lager oder aus der Werkstatt.
            </p>
          </div>
          <div className="mt-10 flex items-center gap-3">
            <button
              onClick={() => navigate('ueber-uns')}
              data-testid="story-more-btn"
              className="text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center gap-2 transition-colors"
            >
              Mehr über LAIzyBlueprint <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* ROI */}
      <ScrollReveal>
      <div id="roi">
        <RoiCalculator onCta={onRegister} />
      </div>
      </ScrollReveal>

      {/* PRICING */}
      <ScrollReveal>
      <section id="pricing" className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-3">Preise</div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Klares Modell. Keine Überraschungen.
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Eine Lizenz pro Firma, optional erweiterbar pro Mitarbeiter, der die Engine aktiv nutzt.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {/* Firmen-Lizenz */}
            <div className="relative p-7 bg-zinc-900 border-2 border-blue-500/40 rounded-2xl shadow-lg shadow-blue-500/5" data-testid="pricing-firma">
              <div className="absolute -top-3 left-7 px-2.5 py-0.5 bg-blue-500 text-white font-mono text-[10px] rounded">
                Basis-Paket
              </div>
              <div className="flex items-center gap-2.5 mb-2 mt-1.5">
                <div className="w-9 h-9 bg-blue-500/15 rounded-lg flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-blue-400" />
                </div>
                <h3 className="font-bold text-white text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Firmen-Lizenz
                </h3>
              </div>
              <p className="text-zinc-400 text-sm mb-6">Voller Engine-Zugang für ein Unternehmen.</p>
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className="text-4xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {fmt(pricing.owner_price)} €
                </span>
                <span className="text-zinc-500 text-sm">netto / Monat</span>
              </div>
              <p className="text-zinc-600 text-xs mb-6">pro Firma · monatlich kündbar</p>
              <ul className="space-y-2.5 mb-7 text-sm">
                {[
                  'Unbegrenzte Grundriss-Analysen',
                  'Vollständige LV-Erstellung (PDF-Export)',
                  'EngineShowcase, Schnitt-Analyse, alle Werkzeuge',
                  '1 Hauptnutzer inklusive',
                  'E-Mail-Support innerhalb von 24h',
                  'DSGVO-konformes Hosting in der EU',
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-zinc-200">
                    <Check className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={onRegister}
                data-testid="pricing-firma-cta"
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
              >
                Firmen-Lizenz starten <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Pro weiterer Mitarbeiter */}
            <div className="relative p-7 bg-zinc-900/60 border border-zinc-800 rounded-2xl" data-testid="pricing-user">
              <div className="absolute -top-3 left-7 px-2.5 py-0.5 bg-zinc-800 border border-zinc-700 text-zinc-300 font-mono text-[10px] rounded">
                Add-on
              </div>
              <div className="flex items-center gap-2.5 mb-2 mt-1.5">
                <div className="w-9 h-9 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                  <Plus className="w-4 h-4 text-emerald-400" />
                </div>
                <h3 className="font-bold text-white text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Pro weiterem Mitarbeiter
                </h3>
              </div>
              <p className="text-zinc-400 text-sm mb-6">Für jeden zusätzlichen Mitarbeiter, der die Engine aktiv nutzt.</p>
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className="text-4xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {fmt(pricing.member_price)} €
                </span>
                <span className="text-zinc-500 text-sm">netto / Monat</span>
              </div>
              <p className="text-zinc-600 text-xs mb-6">pro zusätzlichem Nutzer · jederzeit erweiterbar</p>
              <ul className="space-y-2.5 mb-7 text-sm">
                {[
                  'Eigener Account innerhalb der Firmen-Lizenz',
                  'Individuelle Berechtigungen pro Nutzer',
                  'Alle Engine-Features ohne Einschränkung',
                  'Gemeinsame Projektverwaltung im Team',
                  'Aktivität wird zentral abgerechnet',
                  'Nutzer können wieder entfernt werden',
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-zinc-200">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('kontakt')}
                data-testid="pricing-user-cta"
                className="w-full py-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white font-medium rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
              >
                Team-Setup besprechen <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Hinweis */}
          <div className="mt-10 max-w-3xl mx-auto text-center">
            <p className="text-zinc-500 text-xs leading-relaxed">
              Alle Preise verstehen sich netto zzgl. gesetzlicher Mehrwertsteuer. Beispielrechnung für ein
              5-Personen-Team: {fmt(pricing.owner_price)} € (Firmen-Lizenz) + 4 × {fmt(pricing.member_price)} € (zusätzliche Mitarbeiter) ={' '}
              <span className="text-zinc-300 font-semibold">{teamExample} € netto / Monat</span>. Erste Analyse kostenlos &
              unverbindlich testbar.
            </p>
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* FINAL CTA */}
      <ScrollReveal>
      <section className="py-24 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-5 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Bereit, Wochen pro Projekt zurückzugewinnen?
          </h2>
          <p className="text-zinc-400 mb-8 leading-relaxed">
            Laden Sie Ihren ersten Grundriss kostenlos hoch — Ergebnis in unter 60 Sekunden.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={onRegister}
              data-testid="final-cta-register"
              className="px-8 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-all hover:shadow-lg hover:shadow-blue-500/20 flex items-center gap-2 text-sm"
            >
              Jetzt testen <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('kontakt')}
              data-testid="final-cta-contact"
              className="px-8 py-3.5 border border-zinc-700 hover:border-zinc-500 text-zinc-200 font-medium rounded-lg transition-colors text-sm"
            >
              Demo anfragen
            </button>
          </div>
        </div>
      </section>
      </ScrollReveal>
    </div>
  );
}
